import { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE = "eatup_cart_v1";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : { restaurant: null, items: [] };
    } catch {
      return { restaurant: null, items: [] };
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(cart));
  }, [cart]);

  const addItem = (restaurant, item, qty = 1) => {
    setCart((prev) => {
      // Different restaurant -> reset cart
      if (prev.restaurant && prev.restaurant.restaurant_id !== restaurant.restaurant_id) {
        return {
          restaurant: {
            restaurant_id: restaurant.restaurant_id,
            name: restaurant.name,
            image_url: restaurant.image_url,
            delivery_fee: restaurant.delivery_fee,
            eta_minutes: restaurant.eta_minutes,
          },
          items: [{ ...item, quantity: qty }],
        };
      }
      const existing = prev.items.find((i) => i.item_id === item.item_id);
      const items = existing
        ? prev.items.map((i) => (i.item_id === item.item_id ? { ...i, quantity: i.quantity + qty } : i))
        : [...prev.items, { ...item, quantity: qty }];
      return {
        restaurant: prev.restaurant || {
          restaurant_id: restaurant.restaurant_id,
          name: restaurant.name,
          image_url: restaurant.image_url,
          delivery_fee: restaurant.delivery_fee,
          eta_minutes: restaurant.eta_minutes,
        },
        items,
      };
    });
  };

  const updateQty = (itemId, qty) => {
    setCart((prev) => {
      if (qty <= 0) {
        const items = prev.items.filter((i) => i.item_id !== itemId);
        return items.length === 0 ? { restaurant: null, items: [] } : { ...prev, items };
      }
      return { ...prev, items: prev.items.map((i) => (i.item_id === itemId ? { ...i, quantity: qty } : i)) };
    });
  };

  const removeItem = (itemId) => updateQty(itemId, 0);
  const clear = () => setCart({ restaurant: null, items: [] });

  // Reorder helper — replace the entire cart with items from a past order.
  // Used by the "Reorder" buttons on Home and Order History.
  const replaceCart = (restaurant, items) => {
    if (!restaurant || !items?.length) return;
    setCart({
      restaurant: {
        restaurant_id: restaurant.restaurant_id,
        name: restaurant.name,
        image_url: restaurant.image_url,
        delivery_fee: restaurant.delivery_fee,
        eta_minutes: restaurant.eta_minutes,
      },
      items: items.map((it) => ({
        item_id: it.item_id,
        name: it.name,
        price: it.price,
        image: it.image || it.image_url || "",
        quantity: it.quantity || 1,
      })),
    });
  };

  const totals = useMemo(() => {
    const subtotal = cart.items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = cart.items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal: Math.round(subtotal * 100) / 100, count };
  }, [cart]);

  return (
    <CartContext.Provider value={{ cart, addItem, updateQty, removeItem, clear, replaceCart, totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
